/**
 * ElasticSearch Filter Builders
 * Pure functions for building ES filter objects - easy to test independently
 */

import type { SearchOptions, EsFilter } from './types';
import { DATA_SOURCE } from '../../data/js/data-sources';
import { TRUST_LEVEL } from '../../data/js/trust-levels';
import { ORGANIC_SOURCE_IDS, type RankingWeights } from './relevance-weights';
import { BROWSABLE_STATUSES, SOLD, HELD, PURCHASED } from '../../data/js/listing-statuses';
import { dealQualityFilters } from '../deal-quality-filter';
import { hiddenItemFilters, UNJUDGED_FILTER } from '../nsfw-list-rules';

// ============================================================================
// Price Filters
// ============================================================================

/**
 * Build price range filter
 */
export function buildPriceFilter(options: Pick<SearchOptions, 'minPrice' | 'maxPrice'>): EsFilter | null {
  const { minPrice, maxPrice } = options;

  if (minPrice === undefined && maxPrice === undefined) {
    return null;
  }

  const priceRange: Record<string, number> = {};
  if (minPrice !== undefined && minPrice > 0) {
    priceRange.gte = minPrice;
  }
  if (maxPrice !== undefined && maxPrice > 0) {
    priceRange.lte = maxPrice;
  }

  if (Object.keys(priceRange).length === 0) {
    return null;
  }

  return { range: { cost: priceRange } };
}

// ============================================================================
// Status Filters
// ============================================================================

/**
 * Build the default status filter.
 *
 * Was `status > 0`, which admitted SOLD and HELD indiscriminately. See
 * BROWSABLE_STATUSES for why that mattered.
 *
 * Both dials are FILTERS as well as weights, because a weight cannot put a
 * document into a result set it was excluded from -- the dial has to open the
 * door before it can decide how far in.
 *
 * SOLD and HELD are not the same thing and are not dialled together, which an
 * earlier version of this function got wrong. Sold is permanent. A hold is a
 * buyer request with a Stripe authorisation behind it, and an uncaptured card
 * authorisation survives seven days and then expires -- so most held items
 * return to the market, and hiding one costs a week of discovery on an item
 * that is probably still for sale. Hence heldMix defaults to 0.5 and soldMix
 * to 0.
 */
export function buildDefaultStatusFilter(soldMix = 0, heldMix = 0): EsFilter {
  const statuses = [...BROWSABLE_STATUSES];
  // PURCHASED is a sale pending too, so it rides with HELD.
  if (heldMix > 0) statuses.push(PURCHASED, HELD);
  if (soldMix > 0) statuses.push(SOLD);
  return { terms: { status: statuses } };
}

/**
 * Build status filter for specific statuses
 */
export function buildStatusFilter(status: number | number[]): EsFilter {
  if (Array.isArray(status)) {
    return { terms: { status } };
  }
  return { term: { status } };
}

/**
 * Build status filter for functionScore must array
 */
export function buildStatusMustFilter(status: number | number[]): EsFilter {
  if (Array.isArray(status)) {
    return { bool: { should: [{ terms: { status } }] } };
  }
  return { bool: { should: [{ term: { status } }] } };
}

// ============================================================================
// Data Source Filters
// ============================================================================

/**
 * Build data source filter
 */
export function buildDataSourceFilter(dataSourceId: number): EsFilter | null {
  if (!dataSourceId || dataSourceId <= 0) {
    return null;
  }
  return { term: { dataSourceId } };
}

// ============================================================================
// Boolean Filters
// ============================================================================

/**
 * Build shippable filter
 */
export function buildShippableFilter(shippable: boolean): EsFilter {
  return { term: { shippable } };
}

/**
 * Build hasPhoto filter
 */
export function buildHasPhotoFilter(hasPhoto: boolean): EsFilter {
  if (hasPhoto) {
    return { range: { hasPhoto: { gt: 0 } } };
  }
  return { term: { hasPhoto: 0 } };
}

// ============================================================================
// User Filters
// ============================================================================

/**
 * Build user ID filter
 */
export function buildUserIdFilter(userId: number): EsFilter {
  return { term: { userId } };
}

/**
 * Build exclude user ID filter
 */
export function buildExcludeUserIdFilter(userId: number): EsFilter {
  return { term: { userId } };
}

/**
 * Build exclude multiple user IDs filter
 */
export function buildExcludeUserIdsFilter(userIds: number[] | undefined): EsFilter | null {
  if (!userIds || userIds.length === 0) {
    return null;
  }
  return { terms: { userId: userIds } };
}

// ============================================================================
// Geo Filters
// ============================================================================

export interface GeoOptions {
  latitude: number;
  longitude: number;
  distance: number;
  distanceStart?: number;
}

/**
 * Build geographic distance filter
 */
export function buildGeoFilter(options: GeoOptions): EsFilter {
  const { latitude, longitude, distance, distanceStart } = options;

  // If distanceStart is provided, create a donut-shaped radius
  if (distanceStart !== undefined && distanceStart > 0) {
    return {
      bool: {
        filter: [
          {
            geo_distance: {
              distance: `${distance}km`,
              location: { lat: latitude, lon: longitude }
            }
          }
        ],
        must_not: [
          {
            geo_distance: {
              distance: `${distanceStart}km`,
              location: { lat: latitude, lon: longitude }
            }
          }
        ]
      }
    };
  }

  return {
    geo_distance: {
      distance: `${distance}km`,
      location: { lat: latitude, lon: longitude }
    }
  };
}

// ============================================================================
// Membership Filters
// ============================================================================

export interface MembershipOptions {
  membershipType: number | number[];
}

/**
 * Build membership type filter.
 *
 * The membershipType param always resolves against `partnerMembershipTypes`
 * (sourced from PARTNER_FEATURED_LISTINGS), i.e. "listings explicitly featured
 * for this partner" — the partner-widget mechanism. It intentionally does NOT
 * key off the seller's `user.membershipType`; that field is buyer-attribution
 * only and has no search role.
 */
export function buildMembershipFilter(options: MembershipOptions): EsFilter {
  const { membershipType } = options;
  const fieldToFilter = 'partnerMembershipTypes';

  if (Array.isArray(membershipType)) {
    return {
      bool: {
        should: membershipType.map(mt => ({ term: { [fieldToFilter]: mt } })),
        minimum_should_match: 1
      }
    };
  }

  return { term: { [fieldToFilter]: membershipType } };
}

/**
 * Build featured listings filter (when no membershipType is specified).
 *
 * "Featured" here means platform-wide featured — a listing with a
 * `featuredDate` (from SupplyListings), independent of any partner. This
 * matches the rest of the pipeline, where "not featured" is
 * `must_not exists featuredDate` and the doc's `isFeatured` is `!!featuredDate`.
 * Partner-specific featuring is a separate dimension (the membershipType param
 * → `partnerMembershipTypes`).
 */
export function buildFeaturedOnlyFilter(): EsFilter {
  return { exists: { field: 'featuredDate' } };
}

// ============================================================================
// Category Filters
// ============================================================================

/**
 * Build category ID filter
 */
export function buildCategoryFilter(categoryId: number | number[]): EsFilter {
  const categoryIds = Array.isArray(categoryId) ? categoryId : [categoryId];

  if (categoryIds.length === 1) {
    return { term: { categoryId: categoryIds[0] } };
  }

  return {
    bool: {
      should: categoryIds.map(id => ({ term: { categoryId: id } }))
    }
  };
}

/**
 * Build category group filter
 */
export function buildCategoryGroupFilter(categoryGroupId: number): EsFilter {
  return { term: { categoryGroupId } };
}

/**
 * Build exclude category IDs filter
 */
export function buildExcludeCategoryFilter(
  excludeIds: number | number[],
  includeIds?: number | number[]
): EsFilter | null {
  const excludeArray = Array.isArray(excludeIds) ? excludeIds : [excludeIds];
  const includeArray = includeIds
    ? (Array.isArray(includeIds) ? includeIds : [includeIds])
    : [];

  // Only exclude if not explicitly included
  const idsToExclude = excludeArray.filter(id => !includeArray.includes(id));

  if (idsToExclude.length === 0) {
    return null;
  }

  return {
    bool: {
      should: idsToExclude.map(id => ({ term: { categoryId: id } }))
    }
  };
}

/**
 * Build exclude category group filter (allows featured to show)
 */
export function buildExcludeCategoryGroupFilter(
  excludeGroupIds: number | number[],
  currentCategoryGroupId?: number
): EsFilter | null {
  const excludeArray = Array.isArray(excludeGroupIds) ? excludeGroupIds : [excludeGroupIds];

  // Check if any excluded group is explicitly included
  const isExplicitlyIncluded = currentCategoryGroupId && excludeArray.includes(currentCategoryGroupId);

  if (isExplicitlyIncluded) {
    return null;
  }

  // Exclude listings that are in excluded category groups AND not featured
  return {
    bool: {
      must: [
        {
          bool: {
            should: excludeArray.map(id => ({ term: { categoryGroupId: id } }))
          }
        },
        {
          bool: {
            must_not: { exists: { field: 'featuredDate' } }
          }
        }
      ]
    }
  };
}

/**
 * Build strict exclude category group filter (excludes even if featured)
 */
export function buildStrictExcludeCategoryGroupFilter(
  excludeGroupIds: number | number[],
  currentCategoryGroupId?: number
): EsFilter | null {
  const excludeArray = Array.isArray(excludeGroupIds) ? excludeGroupIds : [excludeGroupIds];

  // Check if any excluded group is explicitly included
  const isExplicitlyIncluded = currentCategoryGroupId && excludeArray.includes(currentCategoryGroupId);

  if (isExplicitlyIncluded) {
    return null;
  }

  return {
    bool: {
      should: excludeArray.map(id => ({ term: { categoryGroupId: id } }))
    }
  };
}

// ============================================================================
// Item Condition Filter
// ============================================================================

/**
 * Build item condition filter
 */
export function buildItemConditionFilter(conditions: number[]): EsFilter {
  return {
    bool: {
      should: conditions.map(condition => ({ term: { itemCondition: condition } }))
    }
  };
}

// ============================================================================
// Tag Filter
// ============================================================================

/**
 * Build tags filter
 */
export function buildTagsFilter(tags: string[]): EsFilter {
  return {
    bool: {
      should: tags.map(tag => ({ term: { tags: tag.toLowerCase() } })),
      minimum_should_match: 1
    }
  };
}

// ============================================================================
// Exclude Listing Filter
// ============================================================================

/**
 * Build exclude listing ID filter
 */
export function buildExcludeListingFilter(listingId: number): EsFilter {
  return { term: { listingId } };
}

/**
 * Build exclude-listing-IDs filter for a set of already-shown listings.
 * One `terms` clause (a single cached bitset) rather than N `term` clauses —
 * scales cleanly as the exclude set grows during fallback pagination.
 */
export function buildExcludeListingIdsFilter(listingIds: number[] | undefined): EsFilter | null {
  if (!listingIds || listingIds.length === 0) {
    return null;
  }
  return { terms: { listingId: listingIds } };
}

// ============================================================================
// Composite Filter Builder
// ============================================================================

export interface FilterBuildResult {
  andFilters: EsFilter[];
  mustNotFilters: EsFilter[];
  functionScoreMust: EsFilter[];
}

/**
 * Build all filters from search options
 * Returns categorized filters ready for ES query construction
 */
/** Search option to the document field it filters. */
export const ATTRIBUTE_FILTER_FIELDS = {
  gender: 'gender',
  ageGroup: 'age_group',
  size: 'size',
  color: 'colors',
  brand: 'brand',
  material: 'materials',
} as const;

export type AttributeFilterKey = keyof typeof ATTRIBUTE_FILTER_FIELDS;

/**
 * One terms filter per attribute the options set, except `skip`: a facet
 * counts its own options under every OTHER filter, so choosing Red still
 * shows how many Blue there are.
 */
export function buildAttributeFilters(options: Partial<Record<AttributeFilterKey, string[]>>, skip?: AttributeFilterKey): EsFilter[] {
  const filters: EsFilter[] = [];
  for (const [key, field] of Object.entries(ATTRIBUTE_FILTER_FIELDS) as Array<[AttributeFilterKey, string]>) {
    const values = options[key];
    if (key === skip || !values || values.length === 0) continue;
    filters.push({ terms: { [field]: values } });
  }
  return filters;
}

/**
 * The tag and type filters (utils/tag-facet-param.ts): one terms filter per
 * section, so a section's values match any and the sections all must match.
 * `type` filters the category id; the rest the tags. `skip` leaves one
 * section out, for that section's own counts.
 */
export function buildTagFacetFilters(tagFacets: Record<string, string[]> | undefined, skip?: string): EsFilter[] {
  const filters: EsFilter[] = [];
  for (const [key, values] of Object.entries(tagFacets ?? {})) {
    if (key === skip || values.length === 0) continue;
    if (key === 'type') {
      const ids = values.map(Number).filter(n => Number.isInteger(n) && n > 0);
      if (ids.length) filters.push({ terms: { categoryId: ids } });
    } else {
      filters.push({ terms: { tags: values.map(v => v.toLowerCase()) } });
    }
  }
  return filters;
}

/**
 * Retired listings out: created before `maxListingAgeDays` ago. Null when the
 * dial is off, or on a seller's own page, which shows everything they have
 * listed whatever its age. Rounded to the day so the clause is the same all day
 * and the cluster can cache it.
 */
export function buildMaxListingAgeFilter(options: Pick<SearchOptions, 'maxListingAgeDays' | 'userId'>): EsFilter | null {
  const days = options.maxListingAgeDays;
  if (!days || !(days > 0) || options.userId) return null;
  return { range: { createdDate: { gte: `now-${Math.floor(days)}d/d` } } };
}

/**
 * The ranking dials that act as FILTERS, copied from the weights a request runs
 * under onto its options. Anything the caller set already wins.
 *
 * Until 2026-10-09 nothing did this: SearchOptions had the fields and the
 * filters read them, but no caller filled them, so externalMix at 0 never
 * excluded a feed. Callers resolve the weights first now, so an experiment arm's
 * dials filter its own traffic.
 *
 * soldMix and heldMix are left out on purpose. Opening the door to SOLD or HELD
 * here would admit them at FULL weight: the score script does not weight by
 * status yet, so a held listing would rank as if it were live.
 */
export function withRankingDials(
  options: SearchOptions,
  weights: Pick<RankingWeights, 'externalMix' | 'maxListingAgeDays'> & Partial<Pick<RankingWeights, 'featuredWindowDays'>>
): SearchOptions {
  return {
    ...options,
    externalMix: options.externalMix ?? weights.externalMix,
    maxListingAgeDays: options.maxListingAgeDays ?? weights.maxListingAgeDays,
    ...(options.featuredLane
      ? { featuredWindowDays: options.featuredWindowDays ?? weights.featuredWindowDays }
      : {}),
  };
}

/**
 * Our own listings nobody can check out: the seller cannot accept payments
 * (data/js/trust-levels.ts — only VERIFIED, 2, can), so the product page shows
 * Contact Seller instead of Buy Now. 2026-10-11: a $500 Jeep with a blown
 * engine from such a seller was the first tile on home.
 */
export function buildUnbuyableOrganicFilter(): EsFilter {
  return {
    bool: {
      filter: [{ terms: { dataSourceId: [...ORGANIC_SOURCE_IDS] } }],
      must_not: [{ term: { 'user.trustLevel': TRUST_LEVEL.VERIFIED } }],
    },
  };
}

/**
 * The listings a featured lane holds: organic, and featured inside the window.
 *
 * Click-out and adoption picks are left out on purpose. Featuring is a summand
 * that clears every source tier, so a featured dealer car outranked every
 * listing we sell; outside the lane it ranks where its tier puts it.
 */
export function buildFeaturedLaneFilter(windowDays: number): EsFilter {
  const hours = Math.max(1, Math.round(windowDays * 24));
  return {
    bool: {
      filter: [
        { range: { featuredDate: { gte: `now-${hours}h` } } },
        { terms: { dataSourceId: [...ORGANIC_SOURCE_IDS] } },
      ],
    },
  };
}

export function buildAllFilters(options: SearchOptions): FilterBuildResult {
  const andFilters: EsFilter[] = [];
  const mustNotFilters: EsFilter[] = [];
  const functionScoreMust: EsFilter[] = [];

  // Price range
  const priceFilter = buildPriceFilter(options);
  if (priceFilter) {
    andFilters.push(priceFilter);
  }

  // Status filter. `soldMix` opens the door to sold listings; the weight that
  // decides how far they rank is applied in the score script, not here.
  if (options.status === undefined) {
    andFilters.push(buildDefaultStatusFilter(options.soldMix, options.heldMix));
  }

  // externalMix at zero means EXCLUDED, not merely ranked last. A weight of 0
  // makes a listing score 0, which sorts it to the tail and still returns it --
  // and a results page whose tail is entirely feed listings is not "no
  // externals". An explicit dataSourceId query is left alone: someone asking
  // for one feed by id has already overridden the mix.
  if (options.externalMix === 0 && !options.dataSourceId) {
    andFilters.push({ terms: { dataSourceId: [...ORGANIC_SOURCE_IDS] } });
  }

  const ageFilter = buildMaxListingAgeFilter(options);
  if (ageFilter) andFilters.push(ageFilter);

  if (options.buyableOnly) mustNotFilters.push(buildUnbuyableOrganicFilter());

  if (options.excludePartnerMembershipType !== undefined) {
    mustNotFilters.push(buildMembershipFilter({ membershipType: options.excludePartnerMembershipType }));
  }

  // 'unboosted' filters nothing: it only switches the featured boost off.
  if (options.featuredLane === 'only' || options.featuredLane === 'exclude') {
    const windowDays = options.featuredWindowDays ?? 0;
    if (windowDays > 0) {
      const lane = buildFeaturedLaneFilter(windowDays);
      if (options.featuredLane === 'only') andFilters.push(lane);
      else mustNotFilters.push(lane);
    } else if (options.featuredLane === 'only') {
      // No window means featuring is off: the lane is empty, not "everything".
      andFilters.push({ bool: { must_not: [{ match_all: {} }] } });
    }
  }

  // DataSource filter
  if (options.dataSourceId && options.dataSourceId > 0) {
    andFilters.push(buildDataSourceFilter(options.dataSourceId)!);
  }

  // Underwear, lingerie and sexual items (utils/not-recommended.ts) are for
  // someone who typed words looking for them, never a row or a browse page.
  // Every category and tag on the /dash/search list too (utils/nsfw-list.ts).
  if (!options.includeNotRecommended) {
    mustNotFilters.push(...hiddenItemFilters());
  }
  // Not judged yet: out of typed search too.
  mustNotFilters.push(UNJUDGED_FILTER);

  // Exclude admin test products (dataSourceId=99) from all searches
  // unless specifically querying for them
  if (options.dataSourceId !== DATA_SOURCE.ADMIN_TEST) {
    mustNotFilters.push({ term: { dataSourceId: DATA_SOURCE.ADMIN_TEST } });
  }

  // Shippable filter
  if (options.shippable !== undefined) {
    andFilters.push(buildShippableFilter(options.shippable));
  }

  // Deals Only / Quality Only (utils/deal-quality-filter.ts)
  andFilters.push(...dealQualityFilters(options));

  // HasPhoto filter
  if (options.hasPhoto !== undefined) {
    andFilters.push(buildHasPhotoFilter(options.hasPhoto));
  }

  // User ID filter
  if (options.userId) {
    andFilters.push(buildUserIdFilter(options.userId));
  }

  // Geo filter
  if (options.latitude !== undefined && options.longitude !== undefined && options.distance !== undefined) {
    andFilters.push(buildGeoFilter({
      latitude: options.latitude,
      longitude: options.longitude,
      distance: options.distance,
      distanceStart: options.distanceStart
    }));
  }

  // Membership filter — always resolves against partner-featured listings.
  if (options.membershipType !== undefined) {
    andFilters.push(buildMembershipFilter({
      membershipType: options.membershipType
    }));
  } else if (options.featured) {
    andFilters.push(buildFeaturedOnlyFilter());
  }

  // Listing attribute filters: gender, age group, size, color, brand, material
  andFilters.push(...buildAttributeFilters(options));
  andFilters.push(...buildTagFacetFilters(options.tagFacets));

  // Tags filter (added to functionScoreMust)
  if (options.tags && options.tags.length > 0) {
    functionScoreMust.push(buildTagsFilter(options.tags));
  }

  // Category filter
  if (options.categoryId) {
    functionScoreMust.push(buildCategoryFilter(options.categoryId));
  }

  // Only a real group: -1 (the For You tab's marker) or 0 (All) filters nothing.
  if (options.categoryGroupId && options.categoryGroupId > 0) {
    functionScoreMust.push(buildCategoryGroupFilter(options.categoryGroupId));
  }

  // Exclude category filters
  if (options.excludeCategoryId) {
    const excludeFilter = buildExcludeCategoryFilter(options.excludeCategoryId, options.categoryId);
    if (excludeFilter) {
      mustNotFilters.push(excludeFilter);
    }
  }

  // Exclude category group (allows featured)
  if (options.excludeCategoryGroupId) {
    const excludeFilter = buildExcludeCategoryGroupFilter(
      options.excludeCategoryGroupId,
      options.categoryGroupId
    );
    if (excludeFilter) {
      mustNotFilters.push(excludeFilter);
    }
  }

  // Strict exclude category group (even if featured)
  if (options.strictExcludeCategoryGroupId) {
    const excludeFilter = buildStrictExcludeCategoryGroupFilter(
      options.strictExcludeCategoryGroupId,
      options.categoryGroupId
    );
    if (excludeFilter) {
      mustNotFilters.push(excludeFilter);
    }
  }

  // Exclude listing ID
  if (options.excludeListingId) {
    mustNotFilters.push(buildExcludeListingFilter(options.excludeListingId));
  }

  // Exclude a set of already-shown listing IDs (fallback / pagination dedup).
  // The builder returns null for a missing or empty list.
  const excludeListingIdsFilter = buildExcludeListingIdsFilter(options.excludeListingIds);
  if (excludeListingIdsFilter) {
    mustNotFilters.push(excludeListingIdsFilter);
  }

  // Exclude user ID
  if (options.excludeUserId) {
    mustNotFilters.push(buildExcludeUserIdFilter(options.excludeUserId));
  }

  // Exclude multiple user IDs (null for a missing or empty list)
  const excludeUserIdsFilter = buildExcludeUserIdsFilter(options.excludeUserIds);
  if (excludeUserIdsFilter) {
    mustNotFilters.push(excludeUserIdsFilter);
  }

  // Item condition filter
  if (options.itemCondition && options.itemCondition.length > 0) {
    const conditionFilter = buildItemConditionFilter(options.itemCondition);
    functionScoreMust.push(conditionFilter);
    andFilters.push(conditionFilter);
  }

  // Status filter in must (if specified)
  if (options.status !== undefined) {
    functionScoreMust.push(buildStatusMustFilter(options.status));
  }

  return { andFilters, mustNotFilters, functionScoreMust };
}
